<template>
  <div>
    <div v-show="isShowErrors">
      <ErrorsForEditContract
        :editContract="editContract"
        :errors="editErrors"/>
    </div>
    <div class="mb-4" v-if="error">
      <server-error :error="error"></server-error>
    </div>
    <div class="b-page__title-wrap pt-4 px-6">
      <CardDataEdit
        :prevRoute="prevRoute"
        :contract="editContract.contract"
        :assetComponents="editContract.assetComponents"
        @onClickSaveEditContract="saveContract"
        @cancelEditContract="onCancelEditContract"
        :editAvailable="isEditAvailable"
        :is-read-only-mode="isReadOnlyMode"
        :is-show-errors="isShowErrors"
        :role="role"
        :use-role="useRole"
        @onClickEditCardData="openEditCardData"
        @onClickEditCardDataSimplified="openEditCardDataSimplified"
        @onClickEditEventsCardData="openEditEventsCardData"
        @onClickDeactivationEvent="openDeactivationEvent"
      />
      <ul class="b-tabs__list b-contract__card-data-tabs-list">
        <li class="b-tabs__list-item">
          <router-link
            class="b-tabs__list-item-link"
            :to="{name: 'AssetsEdit', query: queryParamToAdd}"
          >{{$t('CONTENT.assets.rent_objects')}}</router-link>
        </li>
        <li class="b-tabs__list-item">
          <router-link
            class="b-tabs__list-item-link"
            :to="{name: 'PaymentChartsEdit', query: queryParamToAdd }"
          >{{$t('CONTENT.charts.payments_chart')}}</router-link>
        </li>
        <li class="b-tabs__list-item">
          <router-link
            class="b-tabs__list-item-link"
            :to="{name: 'LiabilitiesEdit', query: queryParamToAdd }"
          >{{$t('CONTENT.charts.liability')}}</router-link>
        </li>
        <li class="b-tabs__list-item">
          <router-link
            class="b-tabs__list-item-link"
            :to="{name: 'ContractVersions', query: queryParamToAdd}"
          >{{$t('CONTENT.contract.contract_versions')}}</router-link>
        </li>
        <li class="b-tabs__list-item">
          <router-link
            class="b-tabs__list-item-link"
            :to="{name: 'ContractEventsFromIntegration', query: queryParamToAdd}"
          >{{$t('CONTENT.contract.events')}}</router-link>
        </li>
      </ul>
      <popup name="contract-data-popup" height="auto" :scrollable="true">
        <CardDataPopup
          :contract="editContract.contract"
          @onSaveEditing="saveCardDataEditing($event)"
          @close="onCloseEditCardData"/>
      </popup>
      <popup name="contract-data-events-popup" height="auto" :scrollable="true">
        <CardDataEventsPopup
          :contract="editContract.contract"
          @onSaveEditing="saveCardDataEventsEditing($event)"
          @close="onCloseEditEventsCardData"/>
      </popup>
      <popup name="deactivation-event-popup" height="auto" :scrollable="true">
        <DeactivationEventPopup
          :contract="editContract.contract"
          @onSaveEditing="saveDeactivationEvent($event)"
          @close="onCloseDeactivationEvent"/>
      </popup>
      <popup name="contract-data-simplified-popup" height="auto" :scrollable="true">
        <CardDataPopupSimplified
          :contract="editContract.contract"
          @onSaveEditing="saveCardDataEditing($event)"
          @close="onCloseEditCardDataSimplified"
        ></CardDataPopupSimplified>
      </popup>
    </div>
    <keep-alive>
      <router-view :baseContractObj="editContract"
                   @updateAssetComponents="updateAssetComponents"
                   @updateCurrentContract="updateCurrentContract"/>
    </keep-alive>
  </div>
</template>

<script>
import _ from "lodash";
import CardDataEdit from "./CardDataEdit";
import CardDataPopup from "./rawEditing/CardDataPopup";
import ErrorsForEditContract from "./ErrorsForEditContract";
import CardDataEventsPopup from "./rawEditing/CardDataEventsPopup";
import ChangeLessors from "../../../../plugins/eventRegistry/models/commands/ChangeLessors";
import LessorComponentShares from "../../../../plugins/assetComponent/models/lessorComponentShares";
import dateConverter from "../../../../utils/converters/date.converter";
import CardDataPopupSimplified from "@/components/registry/contracts/edit/rawEditing/CardDataPopupSimplified";
import DeactivationEventPopup from "@/components/registry/contracts/edit/rawEditing/DeactivationEventPopup.vue";
import {mapGetters} from "vuex";

export default {
  components: {
    CardDataEventsPopup,
    DeactivationEventPopup,
    CardDataPopupSimplified,
    CardDataEdit,
    CardDataPopup,
    ErrorsForEditContract,
  },
  props: {
    prevRoute: {
      type: String
    },
    currentContract: {
      type: Object
    },
    contractEventsLength: {
      type: Number
    },
    editAvailable: {
      type: Boolean
    },
  },
  computed: {
    ...mapGetters("eventRegistryPlugin", ["getDirectoryEvents"]),
    queryParamToAdd() {
      if (this.$route.query.eventId) {
        return { eventId: this.$route.query.eventId };
      }
      return null;
    },
    isShowErrors() {
      return Object.entries(this.editErrors).some(([key, value]) => value)
    }
  },
  created() {
    this.editContract = _.cloneDeep(this.currentContract);
    this.assetComponentCount = this.calcAssetComponentsCount(
      this.currentContract.assetComponents
    );
    this.isEditAvailable = this.contractEventsLength <= this.assetComponentCount;
    this.role = localStorage.getItem('role');
    this.useRole = localStorage.getItem('useRole');
    if (this.role === 'Admin' || this.role === 'Operator') {
      this.isReadOnlyMode = false;
    }
  },
  data() {
    return {
      error: null,
      isEditCardData: false,
      isEditCardDataSimplified: false,
      editContract: null,
      isEditAvailable: false,
      backupContract: null,
      contractObjInitialForEvent: null,
      assetComponentCount: null,
      useRole: true,
      isReadOnlyMode: true,
      role: null,
      editErrors: {
        contract: false,
        assets: false,
        emptyPayments: false,
        payments: false,
        emptyAssets: false,
        noAssetsLessors: false
      },
    };
  },
  methods: {
    updateAssetComponents(assetComponents) {
      this.editContract.assetComponents = [];
      this.editContract.assetComponents = assetComponents;
    },
    openEditCardData() {
      this.isEditCardData = true;
      this.$modal.show('contract-data-popup');
    },
    openEditEventsCardData() {
      this.$modal.show('contract-data-events-popup');
    },
    openDeactivationEvent() {
      this.$modal.show('deactivation-event-popup');
    },
    openEditCardDataSimplified() {
      this.isEditCardDataSimplified = true;
      this.$modal.show('contract-data-simplified-popup');
    },
    onCloseEditCardData() {
      this.isEditCardData = false;
      this.$modal.hide('contract-data-popup');
    },
    onCloseEditEventsCardData() {
      this.$modal.hide('contract-data-events-popup');
    },
    onCloseDeactivationEvent() {
      this.$modal.hide('deactivation-event-popup');
    },
    onCloseEditCardDataSimplified() {
      this.isEditCardDataSimplified = false;
      this.$modal.hide('contract-data-simplified-popup');
    },
    saveCardDataEditing(cardData) {
      for (let prop in cardData) {
        if (
          cardData.hasOwnProperty(prop) &&
          cardData[prop] !== this.editContract.contract[prop]
        ) {
          this.editContract.contract[prop] = cardData[prop];
        }
      }
      this.editContract.contract.lessorCounterpartyIds =
        cardData.lessorCounterpartyIds;
    },
    saveCardDataEventsEditing(cardData) {
      if (cardData.changedCounterparties.length) {
        const changedLessorsIds = [];
        cardData.changedCounterparties.forEach(changedCounterparty => {
          this.currentContract.assetComponents.forEach(assetComponent => {
            assetComponent.lessorComponentShares.forEach(lessorComponentShare => {
              if (lessorComponentShare.lessorId === changedCounterparty.lessorId) {
                changedLessorsIds.push({
                  lessorId: changedCounterparty.lessorId,
                  newLessorId: changedCounterparty.newLessorId,
                  newLessorComponentShareExternalId: changedCounterparty.newLessorComponentShareExternalId,
                  assetComponentId: assetComponent.idInDB
                });
              }
            });
          });
        });
        const command = new ChangeLessors(changedLessorsIds);
        this.$store.commit("eventRegistryPlugin/addOrUpdateCommand", command);
      }

      if (cardData.newLessor.value) {
        this.editContract.assetComponents.forEach((assetComponent) => {
          const newLessor = LessorComponentShares.createNew(
            cardData.newLessor.value,
            dateConverter.convertToString(cardData.newLessor.eventDate),
            assetComponent.endDate,
          );
          newLessor.isNew = true;
          assetComponent.lessorComponentShares.forEach((lessor) => {
            lessor.isEditForEvent = false;
            lessor.isNotChanges = true;
            delete lessor.isNew
          })
          assetComponent.lessorComponentShares
            .push(newLessor);
        });
        this.$store.commit('SET_CONTRACT_EVENT_EDITING_STATE', true);
      }

      if (cardData.removeLessor) {
        this.editContract.assetComponents.forEach((assetComponent) => {
          assetComponent.lessorComponentShares.forEach((lessor) => {
            if (lessor.lessorId === cardData.removeLessor) {
              lessor.isRemove = true;
            } else {
              lessor.isEditForEvent = false;
              lessor.isNotChanges = true;
            }
          })
        });
        this.$store.commit('SET_CONTRACT_EVENT_EDITING_STATE', true);
      }
    },
    async saveContract() {
      if(this.isShowErrors) return
      const currentContactId = this.$route.params.id;
      this.checkLessorComponentSharesDates()
      this.$fileLoadingManager.startLoading();
      try {
        this.error = null;
        await this.$eventRegistryManager.loadContractVersions(currentContactId);

        if (this.isNoEvents()) {
          await this.$store.dispatch("updateContract", {
            contractId: currentContactId,
            data: this.editContract
          });
        } else {
          await this.$store.dispatch("updateContractSimplified", {
            contractId: currentContactId,
            data: this.editContract
          });
        }

        this.currentContract.contract.isFilled = true;
        this.$store.commit("SET_CONTRACT_RAW_EDITING_STATE", false);
        this.$store.commit("SET_CONTRACT_CHANGE_CONDITION_STATE", false);
      } catch (e) {
        this.error = e;
      } finally {
        this.$fileLoadingManager.finishLoading();
      }

      if(!this.error)
      {
        this.$store.commit("SET_CONTRACT_RAW_EDITING_STATE", false);
        this.$store.commit("SET_CONTRACT_RAW_EDITING_SIMPLIFIED_STATE", false);
      }
    },

    checkLessorComponentSharesDates() {
      this.editContract.assetComponents
        .flatMap(assetComponent => assetComponent.lessorComponentShares)
        .forEach(lessorComponentShare => {
          const paymentsStartDates = lessorComponentShare.paymentsParameters
            .map(payment => dateConverter.convertToDate(payment.startDate))

          const minDate = paymentsStartDates.reduce((d1, d2) => d1 < d2 ? d1 : d2)
          lessorComponentShare.startDate = dateConverter.convertToString(minDate)

          const paymentsEndDates = lessorComponentShare.paymentsParameters
            .map(payment => dateConverter.convertToDate(payment.endDate))
          paymentsEndDates.push(dateConverter.convertToDate(lessorComponentShare.endDate))
          const maxDate = paymentsEndDates.reduce((d1, d2) => d1 > d2 ? d1 : d2)
          lessorComponentShare.endDate = dateConverter.convertToString(maxDate)
        })
    },

    onCancelEditContract() {
      this.editContract = _.cloneDeep(this.currentContract);
    },

    isNoEvents() {
      return this.getDirectoryEvents.length == 1;
    },

    calcAssetComponentsCount(assetComponents) {
      let count = 0;
      for (let j = 0; j < assetComponents.length; j++) {
        count += 1;
      }
      return count;
    },
    async getNewDiscountRateForPayments() {
      this.error = null;
      try {
        let contractPreview = await this.$store.dispatch(
          "getContractPreview",
          this.editContract.assetComponents
        );
        return contractPreview;
      } catch (e) {
        this.error = e;
      }
    },
    async setPreviewData() {
      let previewData = await this.getNewDiscountRateForPayments();
      this.editContract.assetComponents
        .forEach(contractAssetComponent => {
          const previewAssetComponent = previewData.assetComponents.find(assetComponent => assetComponent.idInDB === contractAssetComponent.idInDB)
          if(previewAssetComponent === undefined) return

          contractAssetComponent.paymentsParameters.forEach((payment, paymentIdx) => {
            const previewPayment = previewAssetComponent.paymentsParameters.find(previewPayment => payment.idInDB === previewPayment.idInDB)
            if(previewPayment === undefined) return
            this.$set(contractAssetComponent.paymentsParameters, paymentIdx, previewPayment)
          })
        })
    },
    updateCurrentContract() {
      this.$emit('updateCurrentContract');
    },
  },
};
</script>
